import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";

import { ActiveImport } from "./parts/ActiveImport";
import { Control } from "./parts/Control";
import { DataSources } from "./parts/DataSources";
import { Files } from "./parts/Files";
import { ImportHistory } from "./parts/ImportHistory";
import { NewImportPrompt } from "./parts/NewImportPrompt";

import {
    abortImport,
    getImportHistory,
    getImportStatus,
    getTaxYear,
    getTaxYearDataStatus,
    resetTaxYearData
} from "../../../../api/services/taxYear";

import { Col, Page, PageHeader, PageLoader, PopupLoader, Row, Toast } from "@tv2no/ui-react";

export const TaxYearEdit = () => {
    const { year } = useParams();
    const importStatusTimeout = useRef();
    const [selectedFile, setSelectedFile] = useState(null);
    const [busyLoading, setBusyLoading] = useState(true);
    const [taxYear, setTaxYear] = useState(null);
    const [importStatus, setImportStatus] = useState(null);
    const [historyListPageNumber, setHistoryListPageNumber] = useState(0);
    const [busyLoadingHistory, setBusyLoadingHistory] = useState(false);
    const [historyList, setHistoryList] = useState(null);
    const [dataStatus, setDataStatus] = useState(null);
    const [busyResetting, setBusyResetting] = useState(false);

    const fetchDataStatus = useCallback(() => {
        getTaxYearDataStatus(year).then((response) => {
            setDataStatus(response);
        });
    }, [year]);

    const fetchTaxYear = useCallback(
        (callback) => {
            setBusyLoading(true);
            getTaxYear(year).then((response) => {
                setTaxYear(response);
                setBusyLoading(false);
                callback && callback();
            });
        },
        [year]
    );

    const fetchImportStatus = useCallback(() => {
        getImportStatus().then((newStatus) => {
            setImportStatus((currentStatus) => {
                // Refresh import history
                if (!newStatus && currentStatus) {
                    fetchDataStatus();
                    setHistoryListPageNumber(-1);
                    setTimeout(() => {
                        setHistoryListPageNumber(0);
                    }, 500);
                }

                return newStatus;
            });

            if (newStatus) {
                importStatusTimeout.current = setTimeout(fetchImportStatus, 3000);
            }
        });
    }, [fetchDataStatus]);

    const deleteTaxData = () => {
        setBusyResetting(true);
        resetTaxYearData(year).then(() => {
            fetchDataStatus();
            setBusyResetting(false);
            Toast.success("Skattedata ble slettet");
        });
    };

    const handleAbortImport = () => {
        abortImport().then(() => {
            Toast.error("Import was aborted");
        });
    };

    useEffect(() => {
        fetchImportStatus();
        return () => {
            clearTimeout(importStatusTimeout.current);
        };
    }, [fetchImportStatus]);

    useEffect(() => {
        if (historyListPageNumber < 0) {
            return;
        }
        setBusyLoadingHistory(true);
        getImportHistory(year, historyListPageNumber, 5).then((response) => {
            setHistoryList((list) => {
                if (list && list.content && response.number > 0) {
                    response.content = list.content.concat(response.content);
                }
                return response;
            });
            setBusyLoadingHistory(false);
        });
    }, [year, historyListPageNumber]);

    useEffect(fetchDataStatus, [fetchDataStatus, year]);
    useEffect(fetchTaxYear, [fetchTaxYear]);

    return (
        <Page>
            <PageHeader icon="tools" title={`Datagrunnlag ${year}`} subtitle="Skattelister" />

            {busyLoading ? (
                <PageLoader />
            ) : (
                <Row>
                    <Col span={24}>
                        <Control taxYear={taxYear} setTaxYear={setTaxYear} />
                    </Col>
                    <Col span={24}>
                        <Files
                            taxYear={taxYear}
                            setSelectedFile={setSelectedFile}
                            fetchTaxYear={fetchTaxYear}
                            busyImporting={importStatus && importStatus.busy}
                        />
                    </Col>
                    {importStatus ? (
                        <Col span={24}>
                            <ActiveImport
                                taxYear={taxYear}
                                entry={importStatus}
                                onAbortImport={handleAbortImport}
                            />
                        </Col>
                    ) : (
                        <Col span={24}>
                            <ImportHistory
                                taxYear={taxYear}
                                historyList={historyList}
                                pageNumber={historyListPageNumber}
                                setPageNumber={setHistoryListPageNumber}
                                busyLoading={busyLoadingHistory}
                            />
                        </Col>
                    )}
                    <Col span={24}>
                        <DataSources
                            busy={!!importStatus}
                            taxYear={taxYear}
                            dataStatus={dataStatus}
                            deleteTaxData={deleteTaxData}
                        />
                    </Col>

                    {/* POPUP: Start file import */}
                    {selectedFile ? (
                        <NewImportPrompt
                            taxYear={taxYear}
                            importFile={selectedFile}
                            onClose={() => setSelectedFile(null)}
                            fetchImportStatus={fetchImportStatus}
                        />
                    ) : null}
                </Row>
            )}

            {busyResetting && <PopupLoader label="Sletter data..." />}
        </Page>
    );
};

export default TaxYearEdit;
